import {
  Button,
  Input,
  Pill,
  Text,
  Title,
} from "@appquality/appquality-design-system";
import {
  Check2Circle,
  CloudUpload,
  Pencil,
  PersonCircle,
  XCircle,
  Stopwatch,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { getSeverityColor, getSeverityIcon, getStatusColor } from "../utils";
import {
  ActionBar,
  HeaderMeta,
  HeaderRow,
  SeverityAndTypeRow,
} from "../styled";
import { BugData, BugEditValues } from "./types";

interface Props {
  data: BugData;
  isEditing: boolean;
  isEditable: boolean;
  isSaving: boolean;
  isMediaValid: boolean;
  isMetadataValid: boolean;
  minimumMedia: number;
  editedValues: BugEditValues;
  onStartEditing: () => void;
  onSave: () => void;
  onCancel: () => void;
  onTitleChange: (title: string) => void;
}

const BugDetailHeader = ({
  data,
  isEditing,
  isEditable,
  isSaving,
  isMediaValid,
  isMetadataValid,
  minimumMedia,
  editedValues,
  onStartEditing,
  onSave,
  onCancel,
  onTitleChange,
}: Props) => {
  const { t } = useTranslation();
  const severityType = getSeverityColor(data.severity.name);
  const statusType = getStatusColor(data.status.name);
  const SeverityIcon = getSeverityIcon(data.severity.name);

  return (
    <>
      <HeaderRow $editing={isEditing}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <Input
              id="bug-title"
              type="text"
              value={editedValues.title ?? data.title}
              onChange={(val: string) => onTitleChange(val)}
            />
          ) : (
            <>
              <Text className="aq-mb-1">BUG ID: {data.id}</Text>

              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <Title size="m">{data.title}</Title>
                <Pill type={statusType}>{data.status.name}</Pill>
              </div>
            </>
          )}
        </div>

        {isEditable && (
          <ActionBar>
            {!isEditing ? (
              <Button kind="primary" size="sm" onClick={onStartEditing}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <Pencil
                    size={14}
                    style={{ display: "block", marginTop: 0 }}
                  />
                  <span>{t("Edit")}</span>
                </span>
              </Button>
            ) : (
              <>
                <Button
                  kind="primary"
                  size="sm"
                  onClick={onSave}
                  disabled={isSaving || !isMediaValid || !isMetadataValid}
                  title={
                    !isMediaValid
                      ? t("At least {{count}} media files are required", {
                          count: minimumMedia,
                        })
                      : undefined
                  }
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <Check2Circle
                      size={14}
                      style={{ display: "block", marginTop: 0 }}
                    />
                    <span>{isSaving ? t("Saving...") : t("Save")}</span>
                  </span>
                </Button>
                <Button
                  kind="secondary"
                  size="sm"
                  squared={true}
                  onClick={onCancel}
                  disabled={isSaving}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <XCircle
                      size={14}
                      style={{ display: "block", marginTop: 0 }}
                    />
                    <span>{t("Cancel")}</span>
                  </span>
                </Button>
              </>
            )}
          </ActionBar>
        )}
      </HeaderRow>

      <HeaderMeta>
        <span>
          <PersonCircle size={14} />
          {data.tester.name} (T{data.tester.id})
        </span>
        <span title={t("Upload date and time")}>
          <CloudUpload size={14} />
          {t("Uploaded on")}{" "}
          {new Date(data.created_at).toLocaleString(undefined, {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </span>
        <span>
          <Stopwatch size={14} />
          {t("Last seen on")}{" "}
          {new Date(data.last_seen).toLocaleString(undefined, {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </span>
      </HeaderMeta>

      <SeverityAndTypeRow>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            marginRight: "0.5rem",
          }}
        >
          <Title size="xs" color={severityType} className="aq-m-0">
            {data.severity.name}
          </Title>
          <SeverityIcon size={11} color={severityType} />
        </div>
        {data.type?.name && <Title size="xs">{data.type.name}</Title>}
      </SeverityAndTypeRow>
    </>
  );
};

export default BugDetailHeader;
